import {
  defaultSystemCode,
  exists,
  SystemCode,
  SystemCodeFromJSON
} from '@/api'
import { dateTimeToPlainFormat, dateToPlainFormat } from '@/utils'

export interface Order {
  id: number
  bibliographyRealId: number
  orderDate: string // 日付
  status: SystemCode
  tempAcceptDate: string // 日付
  buyBranchCode: SystemCode
  departmentCode: SystemCode
  orderPrice: number
  listPrice: number
  collectionType: SystemCode
  handlingType: SystemCode
  acceptType: SystemCode
  storeType: SystemCode
  shelfType: SystemCode
  equipType: SystemCode
  mark: string
  ndc: string
  book: string
  sub: string
  donorCode: SystemCode
  traderCode: SystemCode
  appendixCode: SystemCode
  processedTime: string // 日付タイム
  processedBranchCode: SystemCode
  processedPlaceCode: SystemCode
  processedSystemUserId: number
  evaluationPrice: number
  managementFlag: SystemCode
  organizeFlag: boolean
  selectionYm: string
  selectionCount: number
  budgetYear: number
  budgetBranchCode: SystemCode
  budgetDepartmentCode: SystemCode
  budgetExpenseCode: SystemCode
  applicantUserId: string
  workMemo: string
  workMemoTime: string // 日付
  workMemoPlaceCode: SystemCode
  displayMemo: string
  displayMemoTime: string // 日付
  displayMemoPlaceCode: SystemCode
  localMemo: string
  localMemoTime: string // 日付
  localMemoPlaceCode: SystemCode
  cancelReason: SystemCode
  storingLimitCode: SystemCode
  sendFlag: boolean
  revision: number
}

export const defaultOrder = (): Order => ({
  id: 0,
  bibliographyRealId: 0,
  orderDate: '',
  status: defaultSystemCode(),
  tempAcceptDate: '',
  buyBranchCode: defaultSystemCode(),
  departmentCode: defaultSystemCode(),
  orderPrice: 0,
  listPrice: 0,
  collectionType: defaultSystemCode(),
  handlingType: defaultSystemCode(),
  acceptType: defaultSystemCode(),
  storeType: defaultSystemCode(),
  shelfType: defaultSystemCode(),
  equipType: defaultSystemCode(),
  mark: '',
  ndc: '',
  book: '',
  sub: '',
  donorCode: defaultSystemCode(),
  traderCode: defaultSystemCode(),
  appendixCode: defaultSystemCode(),
  processedTime: '',
  processedBranchCode: defaultSystemCode(),
  processedPlaceCode: defaultSystemCode(),
  processedSystemUserId: 0,
  evaluationPrice: 0,
  managementFlag: defaultSystemCode(),
  organizeFlag: false,
  selectionYm: '',
  selectionCount: 0,
  budgetYear: 0,
  budgetBranchCode: defaultSystemCode(),
  budgetDepartmentCode: defaultSystemCode(),
  budgetExpenseCode: defaultSystemCode(),
  applicantUserId: '',
  workMemo: '',
  workMemoTime: '', // 日付
  workMemoPlaceCode: defaultSystemCode(),
  displayMemo: '',
  displayMemoTime: '',
  displayMemoPlaceCode: defaultSystemCode(),
  localMemo: '',
  localMemoTime: '',
  localMemoPlaceCode: defaultSystemCode(),
  cancelReason: defaultSystemCode(),
  storingLimitCode: defaultSystemCode(),
  sendFlag: false,
  revision: 0
})

export function OrderFromJSON(json: any): Order {
  return {
    id: exists(json, 'id') ? json.id : 0,
    bibliographyRealId: exists(json, 'bibliography_real_id')
      ? json.bibliography_real_id
      : 0,
    orderDate: exists(json, 'order_date')
      ? dateToPlainFormat(json.order_date)
      : '', // 日付
    status: exists(json, 'status')
      ? SystemCodeFromJSON(json.status)
      : defaultSystemCode(),
    tempAcceptDate: exists(json, 'temp_accept_date')
      ? dateToPlainFormat(json.temp_accept_date)
      : '', // 日付
    buyBranchCode: exists(json, 'buy_branch_code')
      ? SystemCodeFromJSON(json.buy_branch_code)
      : defaultSystemCode(),
    departmentCode: exists(json, 'department_code')
      ? SystemCodeFromJSON(json.department_code)
      : defaultSystemCode(),
    orderPrice: exists(json, 'order_price') ? json.order_price : 0,
    listPrice: exists(json, 'list_price') ? json.list_price : 0,
    collectionType: exists(json, 'collection_type')
      ? SystemCodeFromJSON(json.collection_type)
      : defaultSystemCode(),
    handlingType: exists(json, 'handling_type')
      ? SystemCodeFromJSON(json.handling_type)
      : defaultSystemCode(),
    acceptType: exists(json, 'accept_type')
      ? SystemCodeFromJSON(json.accept_type)
      : defaultSystemCode(),
    storeType: exists(json, 'store_type')
      ? SystemCodeFromJSON(json.store_type)
      : defaultSystemCode(),
    shelfType: exists(json, 'shelf_type')
      ? SystemCodeFromJSON(json.shelf_type)
      : defaultSystemCode(),
    equipType: exists(json, 'equip_type')
      ? SystemCodeFromJSON(json.equip_type)
      : defaultSystemCode(),
    mark: exists(json, 'mark') ? json.mark : '',
    ndc: exists(json, 'ndc') ? json.ndc : '',
    book: exists(json, 'book') ? json.book : '',
    sub: exists(json, 'sub') ? json.sub : '',
    donorCode: exists(json, 'donor_code')
      ? SystemCodeFromJSON(json.donor_code)
      : defaultSystemCode(),
    traderCode: exists(json, 'trader_code')
      ? SystemCodeFromJSON(json.trader_code)
      : defaultSystemCode(),
    appendixCode: exists(json, 'appendix_code')
      ? SystemCodeFromJSON(json.appendix_code)
      : defaultSystemCode(),
    processedTime: exists(json, 'processed_time')
      ? dateTimeToPlainFormat(json.processed_time)
      : '', // 日付タイム
    processedBranchCode: exists(json, 'processed_branch_code')
      ? SystemCodeFromJSON(json.processed_branch_code)
      : defaultSystemCode(),
    processedPlaceCode: exists(json, 'processed_place_code')
      ? SystemCodeFromJSON(json.processed_place_code)
      : defaultSystemCode(),
    processedSystemUserId: exists(json, 'processed_system_user_id')
      ? json.processed_system_user_id
      : 0,
    evaluationPrice: exists(json, 'evaluation_price')
      ? json.evaluation_price
      : 0,
    managementFlag: exists(json, 'management_flag')
      ? SystemCodeFromJSON(json.management_flag)
      : defaultSystemCode(),
    organizeFlag: exists(json, 'organize_flag') ? json.organize_flag : false,
    selectionYm: exists(json, 'selection_ym') ? json.selection_ym : '',
    selectionCount: exists(json, 'selection_count') ? json.selection_count : 0,
    budgetYear: exists(json, 'budget_year') ? json.budget_year : 0,
    budgetBranchCode: exists(json, 'budget_branch_code')
      ? SystemCodeFromJSON(json.budget_branch_code)
      : defaultSystemCode(),
    budgetDepartmentCode: exists(json, 'budget_department_code')
      ? SystemCodeFromJSON(json.budget_department_code)
      : defaultSystemCode(),
    budgetExpenseCode: exists(json, 'budget_expense_code')
      ? SystemCodeFromJSON(json.budget_expense_code)
      : defaultSystemCode(),
    applicantUserId: exists(json, 'applicant_user_id')
      ? json.applicant_user_id
      : '',
    workMemo: exists(json, 'work_memo') ? json.work_memo : '',
    workMemoTime: exists(json, 'work_memo_time')
      ? dateTimeToPlainFormat(json.work_memo_time)
      : '', // 日付タイム
    workMemoPlaceCode: exists(json, 'work_memo_place_code')
      ? SystemCodeFromJSON(json.work_memo_place_code)
      : defaultSystemCode(),
    displayMemo: exists(json, 'display_memo') ? json.display_memo : '',
    displayMemoTime: exists(json, 'display_memo_time')
      ? dateTimeToPlainFormat(json.display_memo_time)
      : '', // 日付タイム
    displayMemoPlaceCode: exists(json, 'display_memo_place_code')
      ? SystemCodeFromJSON(json.display_memo_place_code)
      : defaultSystemCode(),
    localMemo: exists(json, 'local_memo') ? json.local_memo : '',
    localMemoTime: exists(json, 'local_memo_time')
      ? dateTimeToPlainFormat(json.local_memo_time)
      : '', // 日付
    localMemoPlaceCode: exists(json, 'local_memo_place_code')
      ? SystemCodeFromJSON(json.local_memo_place_code)
      : defaultSystemCode(),
    cancelReason: exists(json, 'cancel_reason')
      ? SystemCodeFromJSON(json.cancel_reason)
      : defaultSystemCode(),
    storingLimitCode: exists(json, 'storing_limit_code')
      ? SystemCodeFromJSON(json.storing_limit_code)
      : defaultSystemCode(),
    sendFlag: exists(json, 'send_flag') ? json.send_flag : false,
    revision: exists(json, 'revision') ? json.revision : 0
  }
}
